/**
 * An action on a session, such as Undo, Edit from here, or Fork from latest
 * message. A blocked one keeps its reason for the tooltip, and `run` does
 * nothing while `disabledReason` is set.
 */
export type SessionAction = {
  disabledReason?: string;
  /** Running it interrupts the current turn first. */
  stopsTurn?: true;
  /** It is running: its control shows that until the request answers. */
  pending?: true;
  run: () => void;
};

/** Edit from here and Fork from here on one prompt bubble. */
export type PromptActions = { edit?: SessionAction; fork?: SessionAction };

/** The shared rewind and fork policies' answer for one action. */
type ActionDecision = { ok: true; stopsTurn?: true } | { ok: false; reason: string };

/**
 * Whether two actions show the same thing. A prompt bubble keeps the one it
 * already has: Edit from here runs through the rewind hook's latest state, and
 * Fork from here only reads its turn's saved prompt, which never changes.
 */
export function sameSessionAction(
  a: SessionAction | undefined,
  b: SessionAction | undefined,
): boolean {
  return (
    a === b ||
    (a !== undefined &&
      b !== undefined &&
      a.disabledReason === b.disabledReason &&
      a.stopsTurn === b.stopsTurn &&
      a.pending === b.pending)
  );
}

export function blockedAction(reason: string): SessionAction {
  return { disabledReason: reason, run: () => {} };
}

export function decidedAction(decision: ActionDecision, run: () => void): SessionAction {
  if (!decision.ok) {
    return blockedAction(decision.reason);
  }
  return decision.stopsTurn ? { stopsTurn: true, run } : { run };
}

/**
 * An action's tooltip: why it is blocked, or its label. Its confirmation says
 * when it stops running work, so the tooltip does not repeat that.
 */
export function actionTitle(label: string, action: SessionAction): string {
  return action.disabledReason ?? label;
}

/** Each prompt bubble's Edit from here and Fork from here, keyed by bubble ID. */
export function promptActions(
  edits: ReadonlyMap<string, SessionAction>,
  forks: ReadonlyMap<string, SessionAction>,
): Map<string, PromptActions> {
  const actions = new Map<string, PromptActions>();
  for (const [id, edit] of edits) {
    actions.set(id, { edit });
  }
  for (const [id, fork] of forks) {
    actions.set(id, { ...actions.get(id), fork });
  }
  return actions;
}
